import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Button, Stack } from "@mui/material";
import { arrayOf, bool, element, func, node, oneOfType, shape, string } from "prop-types";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { Edit, Visibility } from "@mui/icons-material";
import AddToLibrary from "../components/shared/AddToLibrary";
import Authenticated from "./Authenticated";
import { Fragment } from "react";
import TagsRelationshipContainer from "../components/pages/Cuisine/TagsContainer";
import { Link } from "react-router-dom";
import LanguageSwitcher from "../components/LanguageSwitcher";
import RecipeSummary from "../components/RecipeSummary";
import AlertBox from "../components/AlertBox";
import DeleteDialog from "../components/DeleteDialog";
import Wysiwyg from "../components/shared/Wysiwyg";
import { insightsDesignEnabled } from "../common/utils/insightsDesignEnabled";

const ViewPage = ({
  children,
  title,
  description,
  summaries = [],
  instructionMessage,
  breadcrumbsLinks = [],
  actionOptions = [],
  onDelete,
  addToLibrary = {},
  relationships = [],
  language = false,
  setLanguage = () => {},
  editUrl,
  previewUrl,
  breadcrumbsClassName,
  showLanguageSwitcher = true,
}) => {
  return (
    <Authenticated
      language={language}
      setLanguage={setLanguage}
    >
      <Box
        sx={{
          flexGrow: 1,
        }}
      >
        <Stack
          spacing={1}
          direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'row' }}
          justifyContent={"space-between"}
        >
          <Stack
            direction={"column"}
            spacing={1}
          >
            <Typography
              variant="h4"
              component="h4"
              sx={{
                lineHeight: 1.2,
                mb: 2,
                ...(insightsDesignEnabled()
                  ? {
                      "& .wysiwyg strong": {
                        color: "primary.main",
                        fontWeight: "inherit",
                      },
                    }
                  : {}),
              }}
            >
              <Wysiwyg>{title}</Wysiwyg>
            </Typography>
            <Box className={breadcrumbsClassName || undefined}>
              <Breadcrumbs home links={breadcrumbsLinks} />
            </Box>
          </Stack>

          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
          >
            <AddToLibrary
              junctionTable={addToLibrary.junctionTable}
              filter={addToLibrary.filter}
              addPayload={addToLibrary.addPayload}
              removePayload={addToLibrary.removePayload}
            />

            {
              !!editUrl &&
              <Link
                to={editUrl}
                style={{ textDecoration: 'none' }}
              >
                <Button
                  variant="outlined"
                >
                  <Edit />
                </Button>
              </Link>
            }

            {
              !!previewUrl &&
              <a
                href={previewUrl}
                target="_blank"
              >
                <Button
                  variant="outlined"
                  sx={{
                    borderRadius: {
                      xs: 0,
                      md: '2rem',
                    },
                    color: '#000',
                    background: '#efeeee',
                    borderColor: '#e0e0e0',
                  }}
                  size={'small'}
                  endIcon={
                    <Visibility />
                  }
                >
                  Preview
                </Button>
              </a>
            }

            {!!onDelete && <DeleteDialog />}
          </Stack>
        </Stack>

        {relationships?.length > 0 && (
          <Stack
            direction={"row"}
            spacing={1}
            sx={{
              my: 2,
            }}
          >
            {relationships.map((relationship) => (
              <Fragment key={relationship.name}>
                <TagsRelationshipContainer
                  name={relationship.name}
                  description={relationship.description}
                  count={relationship.count}
                  columns={relationship.columns}
                  junctionTable={relationship.junctionTable}
                  mainTable={relationship.mainTable}
                  mainTablePayload={relationship.mainTablePayload}
                  mainTableResponseHandler={
                    relationship.mainTableResponseHandler
                  }
                  payload={relationship.payload}
                  requestHandler={relationship.requestHandler}
                  responseHandler={relationship.responseHandler}
                />
              </Fragment>
            ))}
          </Stack>
        )}

        {instructionMessage && <AlertBox message={instructionMessage} />}

        <RecipeSummary summaryValues={summaries} />

        {showLanguageSwitcher ? (
          <Box
            sx={{
              my: 2,
              display: {
                xs: 'none',
                md: 'block',
              },
            }}
          >
            <LanguageSwitcher
              selectedLanguageProp={language}
              setSelectedLanguage={setLanguage}
              prefix='View in '
            />
          </Box>
        ) : null}

        {children}
      </Box>
    </Authenticated>
  );
};

ViewPage.propTypes = {
  children: node.isRequired,
  title: string.isRequired,
  description: string,
  breadcrumbsClassName: string,
  showLanguageSwitcher: bool,
  instructionMessage: string,
  breadcrumbsLinks: arrayOf(
    shape({
      url: string,
      content: oneOfType([string, element]).isRequired,
    })
  ),
  actionOptions: arrayOf(
    shape({
      key: string.isRequired,
      content: oneOfType([string, element]).isRequired,
      onClick: func.isRequired,
    })
  ),
  onDelete: func,
};

export default ViewPage;
